'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { Icon, Tooltip } from '@benzinga/core-ui';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons/faQuestionCircle';
import InputContainer from '../InputContainer';

export interface OptionsProfitCalculatorProps {
  className?: string;
}

interface Values {
  finalResult: number | null;
  lossOutPut: number | null;
  lossPercent: number | null;
  maxLoss: number | null;
  maxProfit: number | null;
  profitOutPut: number | null;
  profitPercent: number | null;
}

export const OptionsProfitCalculator = ({ disableHelp = false }: { disableHelp?: boolean }) => {
  const [isHelpVisible, setIsHelpVisible] = React.useState(false);
  const [values, setValues] = React.useState<Values>({
    finalResult: null,
    lossOutPut: null,
    lossPercent: null,
    maxLoss: null,
    maxProfit: null,
    profitOutPut: null,
    profitPercent: null,
  });

  React.useEffect(() => {
    setChangeValue(values, setValues);
  }, [values]);

  const handleChange = (field: string, value: number) => {
    setValues({
      ...values,
      [field]: value,
    });
  };

  const setChangeValue = (values: Values, setValues: React.Dispatch<React.SetStateAction<Values>>) => {
    if (values.maxProfit && values.profitPercent) {
      const profit = Number(values.maxProfit) * Number(values.profitPercent);
      if (profit !== values.profitOutPut) {
        setValues({
          ...values,
          profitOutPut: profit,
        });
      }
    }
    if (values.maxLoss && values.lossPercent) {
      const loss = Number(values.maxLoss) * Number(values.lossPercent);
      if (loss !== values.lossOutPut) {
        setValues({
          ...values,
          lossOutPut: loss,
        });
      }
    }
    if (values.profitOutPut && values.lossOutPut) {
      const val = Number(values.profitOutPut) - Number(values.lossOutPut);
      if (values.finalResult !== val) {
        setValues({
          ...values,
          finalResult: val,
        });
      }
    }
  };

  return (
    <Container className="options-profit-calculator">
      <div className="header">
        <div className="heading">
          <h6>Options Profit Calculator</h6>
          {!disableHelp && (
            <Tooltip content="See more details.">
              <span
                onClick={() => {
                  setIsHelpVisible(true);
                }}
              >
                <Icon icon={faQuestionCircle} />
              </span>
            </Tooltip>
          )}
        </div>
        <hr />
      </div>
      <div className="main-section">
        <div>
          <div className="inputs-container">
            <InputContainer
              name="Max Loss"
              onChange={value => handleChange('maxLoss', value)}
              prefix="$"
              tooltip="Enter the maximum loss potential for this trade"
              value={values.maxLoss}
            />
            <InputContainer
              name="Max Profit"
              onChange={value => handleChange('maxProfit', value)}
              prefix="$"
              tooltip="Enter the maximum profit potential for this trade"
              value={values.maxProfit}
            />
          </div>
          <div className="inputs-container">
            <InputContainer
              name="Prob. Itm Loss (%)"
              onChange={value => handleChange('lossPercent', value)}
              suffix="%"
              tooltip="Enter the probability of being in the money (ITM) for the max loss."
              value={values.lossPercent}
            />
            <InputContainer
              name="Prob. Itm Profit (%)"
              onChange={value => handleChange('profitPercent', value)}
              suffix="%"
              tooltip="Enter the probability of being in the money (ITM) for the max profit."
              value={values.profitPercent}
            />
          </div>
          <div className="inputs-container">
            <InputContainer
              allowNegative={true}
              className="max-loss-input"
              disabled={true}
              name="Max Loss After 100 Trades"
              prefix="$"
              value={values.lossOutPut ? `-${values.lossOutPut}` : null}
            />
            <InputContainer
              className="max-profit-input"
              disabled={true}
              name="Max Profit After 100 Trades"
              value={values.profitOutPut ? values.profitOutPut : null}
            />
          </div>
        </div>
        <div className="expected-outcome-result-container">
          <InputContainer
            allowNegative={true}
            disabled={true}
            name="Expected Outcome After 100 trades"
            prefix={Number(values.finalResult) < 0 ? '-$' : Number(values.finalResult) > 0 ? '$' : undefined}
            value={values.finalResult ? Math.abs(Number(values.finalResult)) : null}
          />
        </div>
        {isHelpVisible && (
          <div className="help-overlay">
            <button
              className="close-button"
              onClick={() => {
                setIsHelpVisible(false);
              }}
            >
              <Icon icon={faTimes} />
            </button>
            <h4 className="title">How to use?</h4>
            <p>
              The 100x Options Calculator is designed to help you determine if your option trade has positive or
              negative expectancy.
            </p>
            <p>
              It does this by looking at the max risk on your option trade, the probability of hitting your max risk on
              the trade, the max profit on your trade, and the probability of hitting your max profit on the trade. By
              examining these 4 variables, the options calculator can determine if your potential trade has positive or
              negative expectancy.
            </p>
            <p>
              It is important to note this will not work on strategies that do not have a fixed max risk or fixed max
              gain on the trade.
            </p>
            <p>
              To use the calculator, enter the % for hitting your max loss (as a number only) the % for hitting your max
              gain (as a number only), the max loss in $ value for your trade and the max gain in $ value for your
              trade. By entering these 4 variables in, the calculator will calculate if your trade has a positive or
              negative expectancy after 100 trades.
            </p>
          </div>
        )}
      </div>
    </Container>
  );
};

export default OptionsProfitCalculator;

const Container = styled.div`
  &.options-profit-calculator {
    display: flex;
    flex-direction: column;
    position: relative;
    min-width: 0px;
    width: 100%;
    border-width: 0px;
    margin-bottom: 1.5rem;
    overflow-wrap: break-word;
    color: ${({ theme }) => theme.colorPalette.gray600};
    background-color: ${({ theme }) => theme.colorPalette.white};

    .header {
      padding-top: 1.5rem;
      padding-bottom: 1.5rem;
      padding-left: 1.5rem;
      padding-right: 1.5rem;

      .heading {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.75rem;

        h6 {
          font-size: ${({ theme }) => theme.fontSize.base};
          font-weight: ${({ theme }) => theme.fontWeight.bold};
        }
      }

      hr {
        margin-top: 1.5rem;
        border-top: 1px solid ${({ theme }) => theme.colorPalette.blue100};
      }
    }

    .main-section {
      padding: 2.5rem 1rem;
      padding-top: 0px;
      background-color: ${({ theme }) => theme.colorPalette.white};
      flex: 1 1 auto;
      @media (min-width: 992px) {
        padding-left: 2.5rem;
        padding-right: 2.5rem;
      }
    }

    .input-container {
      width: 100%;

      label {
        font-size: ${({ theme }) => theme.fontSize.sm};
        font-weight: ${({ theme }) => theme.fontWeight.bold};
        color: ${({ theme }) => theme.colorPalette.gray600};
      }

      input {
        background-color: ${({ theme }) => theme.colorPalette.gray100};
      }
    }

    .inputs-container {
      width: 100%;
      @media (min-width: 600px) {
        display: flex;
        .input-container {
          margin-bottom: 1rem;
          &:first-of-type {
            margin-right: 1rem;
          }
        }
      }
    }

    .max-loss-input {
      input {
        background-color: ${({ theme }) => theme.colorPalette.red300};
      }
    }

    .max-profit-input {
      input {
        background-color: ${({ theme }) => theme.colorPalette.green300};
      }
    }

    .expected-outcome-result-container {
      margin: 0.75rem 0;
      width: 100%;

      label {
        text-transform: uppercase;
        font-size: ${({ theme }) => theme.fontSize.sm};
        text-align: center;
        display: block;
        margin-bottom: 0.5rem;
        max-width: 100%;
        text-align: center;
      }

      input {
        box-shadow: ${({ theme }) => theme.shadow.sm};
      }
    }

    .help-overlay {
      background-color: ${({ theme }) => theme.colorPalette.gray600};
      color: ${({ theme }) => theme.colorPalette.white};
      font-size: 0.75rem;
      line-height: 1rem;
      padding: 1.5rem;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      position: absolute;

      .close-button {
        justify-content: center;
        align-items: center;
        width: 2.5rem;
        height: 2.5rem;
        display: flex;
        z-index: 10;
        right: -1rem;
        top: -1rem;
        position: absolute;
        box-shadow: ${({ theme }) => theme.shadow.sm};
        border-radius: ${({ theme }) => theme.borderRadius.full};
        background-color: ${({ theme }) => theme.colorPalette.white};
        color: ${({ theme }) => theme.colorPalette.black};

        &:hover {
          background-color: ${({ theme }) => theme.colorPalette.blue100};
        }
      }

      .title {
        margin-bottom: 0.5rem;
        font-size: ${({ theme }) => theme.fontSize.xl};
        font-weight: ${({ theme }) => theme.fontWeight.bold};
      }

      p {
        margin-bottom: 0.5rem;
      }
    }
  }
`;
